import * as React from 'react';
import { OverviewPanel } from './WorkplaceAssessmentActivity/OverviewPanel';
import { Activity, PublicAmorgUser, Survey, UserToken } from '../../amorg-common';
import { SurveyReport } from '../../amorg-common';
import { getSurveyByActivityId, getSurveyReport } from '../../api/surveyClientApi';
import { Tabber } from '../../designSystem/Tabber';
import { SurveyPanel } from './WorkplaceAssessmentActivity/SurveyPanel';
import { SingleColumnLayout } from '../../designSystem/layouts/SingleColumnLayout';
import AnswersPanel from './WorkplaceAssessmentActivity/AnswersPanel';
import { WorkplaceAssessmentComponentProps } from './WorkplaceAssessmentActivity/WorkplaceAssessmentComponentProps';
import { ReportPanel } from './WorkplaceAssessmentActivity/ReportPanel';
import { getActivity } from '../../api/activityClientApi';
import { getUsers } from '../../api/userClientApi';
import { getAuthorizedActiveUserOrRedirect } from '../../api/serverApiHelpers';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { ActionPlansPanel } from './WorkplaceAssessmentActivity/ActionPlansPanel';


type State ={
    loading: boolean,
    activeUser: UserToken,
    users: PublicAmorgUser[],
    activity:Activity,
    survey: Survey,
    surveyReport: SurveyReport,
}

type WorkPlaceAssessmentPageViewProps =
{
    activityId:string
}

export function WorkPlaceAssessmentPageView(props:WorkPlaceAssessmentPageViewProps): JSX.Element {
    const [state, setState] = React.useState<State>(
        {
            loading: true,
            activeUser: null,
            activity: null,
            users: [],
            survey: null,
            surveyReport: null
        }
    )
    const [selectedTab, setSelectedTab] = React.useState('overview');

    React.useEffect(function(){
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        let tab = urlParams.get('tab');
        if(!['overview','survey','answers','actionplans','report'].find(t => t === tab))
            tab = 'overview';
        setSelectedTab(tab);
    }, [window.location.search]);

    React.useEffect(function () {
        updateState();
    }, [props.activityId]);

    async function updateState() {
        setState({...state, loading:true});
        const activeUser = await getAuthorizedActiveUserOrRedirect(['ViewActivity']);
        const activity = await getActivity(props.activityId);
        const users = await getUsers();
        const survey = await getSurveyByActivityId(activity.id);
        if (survey !== null) {
            const surveyReport = await getSurveyReport(survey.id);
            setState({
                loading:false,
                activeUser,
                activity,
                users,
                survey,
                surveyReport
            });
        }
        else {
            setState({
                loading:false,
                activeUser,
                activity,
                users,
                survey: null,
                surveyReport: null
            });
        }
    }

    const componentProps:WorkplaceAssessmentComponentProps = {
        activeUser:state.activeUser,
        users:state.users,
        activity:state.activity,
        surveyLoading:state.loading,
        survey:state.survey,
        surveyReport:state.surveyReport,
        onActivityUpdated:() => {
            updateState();
        },
        onSurveyUpdated:() => updateState()
    };

    return (
        <>
            <LoadingPageOverlay loading={state.loading} />
            {state.activity &&
                <SingleColumnLayout
                    noDivider
                    wide
                    contentContainer={
                        <>
                            <Tabber
                                items={[
                                    { tabName:'overview', text:'Oversigt'},
                                    { tabName:'survey', text:'Spørgeskema', disabled:state.survey===null},
                                    { tabName:'answers', text:'Besvarelser', disabled:state.survey===null},
                                    { tabName:'report', text:'Rapport', disabled:state.survey===null||state.survey.finishedAnswerCount===0},
                                    { tabName:'actionplans', text:'Handleplaner'},
                                ]}
                                selectedTabName={selectedTab}
                                onClick={(tabName) => {
                                    window.history.replaceState(null, '', `/workplaceassessments/${props.activityId}?tab=${tabName}`);
                                    setSelectedTab(tabName);
                                }}
                            />

                            {selectedTab === 'overview' &&
                                <OverviewPanel
                                    {...componentProps} />
                            }

                            {selectedTab === 'survey' && state.survey &&
                                <SurveyPanel {...componentProps} />
                            }

                            {selectedTab === 'answers' && state.survey &&
                                <AnswersPanel {...componentProps} />
                            }

                            {selectedTab === 'report' && state.survey &&
                                <ReportPanel  {...componentProps} />
                            }

                            {selectedTab === 'actionplans' &&
                                <ActionPlansPanel  {...componentProps} />
                            }
                        </>
                    }
                />
            }
        </>
    );
}